/**
 * @file font-field-model.ts
 * @description Правила поля «Шрифт» элемента без разметки (phase19.16, партия
 * SB120 «свобода выбора», этап 0; site-builder.md SB120, SB10; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §4): начертания,
 * которые есть у шрифта, строка «с засечками · 400, 700», состояние своего
 * шрифта (есть / список ещё грузится / удалён), предупреждение о числе
 * шрифтов на странице и сводка для группы.
 */

import {
  isShopFontEntry,
  isShopFontKey,
  SITE_FONT_CATEGORY_LABELS,
  type ElementFont,
  type ElementFontCase,
  type FontRole,
  type SiteFontRegistry,
} from "@chaiberi/shared";

import { pluralRu } from "../plural";
import { WEIGHT_LABELS } from "../site-view/font-weights";

/**
 * Изменение поля одним объектом. Ключ есть со значением `undefined` —
 * «как у роли» (сбросить); ключа нет — не менять.
 */
export interface FontFieldPatch {
  font?: ElementFont;
  weight?: number | undefined;
  case?: ElementFontCase | undefined;
  size?: number | undefined;
  tracking?: number | undefined;
}

/** Три роли сайта в режиме «По роли» — реестром: новая роль = запись. */
export const FONT_CHIP_ROLES: readonly { role: FontRole; label: string }[] = [
  { role: "heading", label: "Заголовки" },
  { role: "body", label: "Основной" },
  { role: "accent", label: "Акцент" },
];

/** Сколько семейств на странице считается нормой (uiux-sb120-fields §4, §9 вопрос 4). */
export const PAGE_FONT_FAMILIES_SOFT_LIMIT = 3;

/** Подсказка у заголовка H1 мельче 24 px — не запрет (SB120). */
export const H1_SIZE_HINT = { below: 24, text: "Заголовок H1 мельче 24 px теряется среди текста. Можно оставить." } as const;

/**
 * Начертания, которые есть у шрифта: у каталога — раздаваемые веса, у своего —
 * загруженные файлы (нет файлов — только обычное); неизвестный ключ — 400, 700.
 */
export function fontWeights(key: string, registry: SiteFontRegistry): number[] {
  const entry = registry.find(key);
  if (!entry) return [400, 700];
  if (isShopFontEntry(entry)) {
    const loaded = [...new Set<number>(entry.files.map((f) => f.weight))].sort((a, b) => a - b);
    return loaded.length > 0 ? loaded : [400];
  }
  return [...entry.weights];
}

export interface WeightChoice {
  value: number;
  /** Число — на 360. */
  short: string;
  /** «Обычное 400» — от sm и для чтеца экрана. */
  full: string;
}

/** Варианты «Начертания» кроме «Как у роли» — только имеющиеся у шрифта. */
export function weightChoices(key: string, registry: SiteFontRegistry): WeightChoice[] {
  return fontWeights(key, registry).map((w) => ({
    value: w,
    short: String(w),
    full: WEIGHT_LABELS[w] ? `${WEIGHT_LABELS[w]} ${w}` : String(w),
  }));
}

/** Строка под названием: «с засечками · 400, 700». */
export function fontMetaLine(key: string, registry: SiteFontRegistry): string {
  const entry = registry.find(key);
  if (!entry) return "";
  const category = SITE_FONT_CATEGORY_LABELS[entry.category].toLowerCase();
  return `${category} · ${fontWeights(key, registry).join(", ")}`;
}

/** Состояние своего шрифта: есть в реестре, список своих ещё не получен, удалён. */
export type CustomFontState = "ok" | "pending" | "deleted";

/** Что знаем о списке своих шрифтов магазина (`useShopFonts`). */
export interface ShopFontsLoad {
  loaded: boolean;
  error: string | null;
}

/**
 * Свой шрифт удалён — только когда это точно известно: ключа нет в каталоге,
 * а у своего (`shop:`) — список своих получен без ошибки. Иначе «грузится».
 */
export function customFontState(key: string, registry: SiteFontRegistry, shop: ShopFontsLoad): CustomFontState {
  if (registry.has(key)) return "ok";
  if (!isShopFontKey(key)) return "deleted";
  return shop.loaded && shop.error === null ? "deleted" : "pending";
}

/** Текст сбоя «шрифт удалён» (✕ в группе). */
export const DELETED_FONT_TEXT = "Шрифт удалён — на сайте сейчас шрифт роли. Выберите другой.";

/** «На странице будет 4 шрифта — загрузка медленнее. Лучше до 3»; в норме — null. */
export function familiesWarning(count: number | undefined): string | null {
  if (count === undefined || count <= PAGE_FONT_FAMILIES_SOFT_LIMIT) return null;
  const word = pluralRu(count, ["шрифт", "шрифта", "шрифтов"]);
  return `На странице будет ${count} ${word} — загрузка медленнее. Лучше до ${PAGE_FONT_FAMILIES_SOFT_LIMIT}.`;
}

/** Состояние поля для заголовка группы: ✕ удалён, ! много шрифтов, иначе null. */
export interface FontFieldStatus {
  tone: "error" | "warning";
  text: string;
}

export function fontFieldStatus(deleted: boolean, warning: string | null): FontFieldStatus | null {
  if (deleted) return { tone: "error", text: DELETED_FONT_TEXT };
  return warning ? { tone: "warning", text: warning } : null;
}
